import { Icon, Button, TextLink } from '@mgmresorts/mgm-ui';
import React, { useCallback, useState, useEffect } from 'react';

import { ActivateAccountMessage } from '../../../components/ui/ActivateAccountMessage';
import { Container } from '../../../components/ui/Container';
import { PageLayout } from '../../../components/ui/PageLayout';
import { WelcomeMGMRewards } from '../../../components/ui/WelcomeMGMRewards';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../../utils/auth/AuthProvider';
import { IDW_FLOW_PARAM } from '../../../utils/constants';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { useUserAccount } from '../../../utils/userAccountContext';
import { ConfirmModal } from '../ConfirmModal';
import { MfaEnrollmentFlowType } from '../Enrollment/types';

import { StyledHeading, StyledBody, StyledEnrollButtonWrapper, StyledFooterLinks, StyledSkipButton } from './styles';

export function PasskeyEnrollmentPage() {
  const redirectToApp = useAppRedirect();
  const params = getPersistedParameters();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const { authClient } = useAuthContext();
  const { mgmId } = useUserAccount();

  const [isPasskeySupported, setIsPasskeySupported] = useState<boolean | null>(null);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  const [showPasskeyErrorModal, setShowPasskeyErrorModal] = useState(false);

  const idwFlow = params?.[IDW_FLOW_PARAM] as MfaEnrollmentFlowType | undefined;

  const pageLoadTitle = idwFlow === 'activate' ? 'active account mfa passkey setup' : 'sign up mfa passkey setup';
  usePageLoadEvent('identity', pageLoadTitle);

  // Check passkey support on mount
  useEffect(() => {
    const check = async () => {
      const isSupported =
        window.PublicKeyCredential && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());

      setIsPasskeySupported(!!isSupported);
    };

    check();
  }, []);

  // Auto-skip: only if passkeys are not supported on this device, skip to welcome/activate screen
  // Feature flag gating is handled at the entry point (EmailVerificationPage/SMSVerificationPage)
  useEffect(() => {
    if (isPasskeySupported === null) {
      return; // Still checking support
    }

    if (!isPasskeySupported) {
      // Device doesn't support passkeys - auto-skip
      if (idwFlow === 'activate') {
        setShowAccountActivatedScreen(true);
      } else {
        setShowWelcomeMgmRewardsScreen(true);
      }
      setTimeout(() => {
        redirectToApp();
      }, 2000);
    }
  }, [isPasskeySupported, idwFlow, redirectToApp]);

  const handleEnrollWithPasskey = useCallback(async () => {
    setIsPasskeyLoading(true);
    await trackEvent('identity – unified MFA CTAs', 'Enroll with passkey', 'unifiedmfactas', {}, true);
    try {
      // Get mgmId from access token
      let userMgmId = mgmId;

      const accessToken = await authClient?.getAccessToken();
      if (accessToken?.claims?.uid) {
        userMgmId = String(accessToken.claims.uid);
      } else {
        throw new Error('Unable to retrieve mgmId from access token');
      }

      const mfaManager = await authClient?.mfa(userMgmId);
      const enrollResult = await mfaManager?.enrollPasskeyAuthenticator();

      if (enrollResult?.success && enrollResult?.data) {
        // Activate passkey (triggers WebAuthn ceremony)
        const activationResult = await mfaManager?.activatePasskeyAuthenticator(
          enrollResult.data.id,
          enrollResult.data,
        );

        if (activationResult?.success) {
          await trackEvent(
            'identity – unified MFA notification',
            'Passkey authentication has been added',
            'unifiedmfanotification',
            { notificationDisplayed: 'Passkey authentication has been added' },
            true,
          );

          // Route to welcome screen
          if (idwFlow === 'activate') {
            setShowAccountActivatedScreen(true);
          } else {
            setShowWelcomeMgmRewardsScreen(true);
          }
          setTimeout(() => {
            redirectToApp();
          }, 2000);
        } else {
          throw new Error('Passkey activation failed');
        }
      } else {
        throw enrollResult?.error;
      }
    } catch (error) {
      setShowPasskeyErrorModal(true);
      console.error('Passkey MFA error:', error);
    } finally {
      setIsPasskeyLoading(false);
    }
  }, [authClient, mgmId, trackEvent, idwFlow, redirectToApp]);

  const handleViewFAQs = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      await trackEvent('identity – unified MFA CTAs', 'View FAQs', 'unifiedmfactas', {}, true);
      window.open(getContentByPath('mfa.passkey.faqUrl'), '_blank');
    },
    [getContentByPath, trackEvent],
  );

  const handlePrivacyPolicy = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      await trackEvent('identity – unified MFA CTAs', 'Privacy policy', 'unifiedmfactas', {}, true);
      window.open(getContentByPath('mfa.passkey.privacyPolicyUrl'), '_blank');
    },
    [getContentByPath, trackEvent],
  );

  const handleSkipForNow = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Skip for now', 'unifiedmfactas', {}, true);
    if (idwFlow === 'activate') {
      setShowAccountActivatedScreen(true);
    } else {
      setShowWelcomeMgmRewardsScreen(true);
    }
    setTimeout(() => {
      redirectToApp();
    }, 2000);
  }, [trackEvent, idwFlow, redirectToApp]);

  const handleModalClose = useCallback(async () => {
    setShowPasskeyErrorModal(false);
  }, []);

  const handleModalConfirm = useCallback(async () => {
    // User skips from the abandonment modal - route to welcome
    await trackEvent(
      'identity – unified MFA CTAs',
      'Skip for now - Skip passkey enrollment',
      'unifiedmfactas',
      {},
      true,
    );
    setShowPasskeyErrorModal(false);
    if (idwFlow === 'activate') {
      setShowAccountActivatedScreen(true);
    } else {
      setShowWelcomeMgmRewardsScreen(true);
    }
    setTimeout(() => {
      redirectToApp();
    }, 2000);
  }, [trackEvent, idwFlow, redirectToApp]);

  // If still loading passkey support, don't render yet
  if (isPasskeySupported === null) {
    return null;
  }

  return (
    <PageLayout
      title='Passkey enrollment'
      testId='route-/mfa/enroll/passkey'
      description='Enroll with passkey for faster sign-in'
    >
      {showAccountActivatedScreen ? (
        <ActivateAccountMessage />
      ) : showWelcomeMgmRewardsScreen ? (
        <WelcomeMGMRewards />
      ) : (
        <Container height='auto' width='458px'>
          <Icon name='checkmark-circle' variant='outlined' size='large' color='#007636' />

          <StyledHeading variant='heading-medium-2' as='h1'>
            {getContentByPath('mfa.passkey.heading')}
          </StyledHeading>

          <StyledBody variant='body-regular-m'>{getContentByPath('mfa.passkey.body')}</StyledBody>

          <StyledEnrollButtonWrapper>
            <Button
              variant='primary'
              size='large'
              onClick={handleEnrollWithPasskey}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleEnrollWithPasskey();
                }
              }}
              testId='enroll-passkey-post-2fa'
              type='button'
              label={getContentByPath('mfa.passkey.enrollWithPasskey')}
              shape='square'
              fullWidth
              loading={isPasskeyLoading}
            />
          </StyledEnrollButtonWrapper>

          <StyledFooterLinks>
            <TextLink onClick={handleViewFAQs} data-testid='view-faqs' variant='small'>
              {getContentByPath('mfa.passkey.faq')}
              <Icon
                name='arrow-right'
                variant='outlined'
                size='small'
                color='#00012B'
                width={14}
                height={14}
                style={{ marginLeft: '4px' }}
              />
            </TextLink>

            <TextLink onClick={handlePrivacyPolicy} data-testid='privacy-policy' variant='small'>
              {getContentByPath('mfa.passkey.privacyPolicy')}
            </TextLink>
          </StyledFooterLinks>

          <StyledSkipButton
            variant='tertiary'
            size='large'
            onClick={handleSkipForNow}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSkipForNow();
              }
            }}
            data-testid='skip-for-now'
            type='button'
          >
            {getContentByPath('mfa.passkey.skipForNow')}
          </StyledSkipButton>

          <ConfirmModal
            isOpen={showPasskeyErrorModal}
            title={getContentByPath('MFA.confirmModal.incompletePasskeyTitle')}
            message={getContentByPath('MFA.confirmModal.incompletePasskeyDescription')}
            cancelLabel={getContentByPath('MFA.confirmModal.tryAgain')}
            confirmLabel={getContentByPath('mfa.passkey.skipForNow')}
            onClose={handleModalClose}
            onConfirm={handleModalConfirm}
            showCloseButton={true}
            testId='passkey-error-modal'
          />
        </Container>
      )}
    </PageLayout>
  );
}

PasskeyEnrollmentPage.displayName = 'PasskeyEnrollmentPage';
